import pino, { type DestinationStream, type Logger } from "pino";

declare const __webpack_require__: any;
declare const __non_webpack_require__: NodeRequire;
import { getLogContext } from "./context";
import {
  createDatadogLocalTransport,
  getStdoutStream,
  isLocalScript,
  isNodeServer,
  isServer,
} from "./utils";

let tracer: any = null;
if (isNodeServer) {
  try {
    // Dynamic require hidden from webpack static analysis — dd-trace is a
    // Node-native module that cannot be bundled by webpack/turbopack.
    const _require =
      typeof __webpack_require__ === "function"
        ? __non_webpack_require__
        : require;
    tracer = _require("dd-trace");
  } catch {
    // dd-trace not available in non-Next.js services
  }
}

const datadogLocalTransport = createDatadogLocalTransport();

const _getAppUrl = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const baseUrl = process.env.APP_URL || process.env.NEXT_PUBLIC_APP_URL || "";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${normalizedBaseUrl}${normalizedPath}`;
};

const _attributeToAppUrlMap: Record<string, string> = {
  studentId: _getAppUrl("/students/:studentId"),
  guardianId: _getAppUrl("/guardians/:guardianId"),
  therapistId: _getAppUrl("/therapists/:therapistId"),
  appointmentId: _getAppUrl("/appointments/:appointmentId"),
};

const loggerConfig = {
  errorKey: "error",
  formatters: {
    // @ts-ignore - pino formatter signature
    level: (level: any) => ({ level }),
  },
  // @ts-ignore
  mixin(params: Record<string, any>, _level: number, loggerInstance?: Logger) {
    // Ambient context (see ./context.ts) goes in first so the per-call merge
    // object, which pino applies on top of the mixin result, wins on conflict.
    const additionalParams: Record<string, any> = { ...getLogContext() };

    if (isNodeServer) {
      try {
        const hasDdFields =
          params.dd || params["dd.trace_id"] || params["dd.span_id"];

        if (!hasDdFields && tracer && typeof tracer.scope === "function") {
          const span = tracer.scope().active();
          if (span && span.context) {
            const traceId = span.context().toTraceId();
            const spanId = span.context().toSpanId();
            if (traceId && spanId) {
              additionalParams.dd = {
                trace_id: traceId,
                span_id: spanId,
                service:
                  process.env.DD_SERVICE || process.env.NEXT_PUBLIC_DD_SERVICE,
                version:
                  process.env.DD_VERSION || process.env.NEXT_PUBLIC_VERSION,
              };
            }
          }
        }
      } catch {
        // Silently fail
      }
    }

    // Pino's mixin receives the per-call merge object in `params` but not the
    // child bindings set via .child()/addAttributes. Merge bindings in so URLs
    // are generated regardless of where the ID was attached.
    const bindings = loggerInstance?.bindings?.() ?? {};
    const combined = { ...bindings, ...params };

    for (const [key, urlTemplate] of Object.entries(_attributeToAppUrlMap)) {
      if (combined[key]) {
        let urlKey;
        if (key.endsWith("Id")) {
          urlKey = key.replace("Id", "Url");
        } else if (key.endsWith("Uid")) {
          urlKey = key.replace("Uid", "Url");
        } else {
          urlKey = key + "Url";
        }
        additionalParams[urlKey] = urlTemplate.replace(
          `:${key}`,
          combined[key] as string,
        );
      }
    }

    return additionalParams;
  },
  serializers: {
    error: (err: Error & { fingerprint?: string }) => ({
      message: err.message,
      stack: err.stack,
      name: err.name,
      type: err.name,
      kind: err.name,
      ...(err.fingerprint !== undefined && { fingerprint: err.fingerprint }),
    }),
  },
  ...(!isServer && {
    browser: {
      write: {
        /* eslint-disable no-console */
        critical: (o: Object) =>
          console.error(JSON.stringify({ ...o, level: "error" })),
        debug: (o: Object) =>
          console.debug(JSON.stringify({ ...o, level: "debug" })),
        error: (o: Object) =>
          console.error(JSON.stringify({ ...o, level: "error" })),
        fatal: (o: Object) =>
          console.error(JSON.stringify({ ...o, level: "error" })),
        info: (o: Object) =>
          console.log(JSON.stringify({ ...o, level: "info" })),
        trace: (o: Object) =>
          console.log(JSON.stringify({ ...o, level: "info" })),
        warn: (o: Object) =>
          console.warn(JSON.stringify({ ...o, level: "warn" })),
        /* eslint-enable no-console */
      },
    },
  }),
};

let logger: Logger;

if (datadogLocalTransport && process.env.NEXT_RUNTIME !== "edge") {
  // @ts-ignore
  logger = pino(
    loggerConfig,
    pino.multistream([
      { stream: getStdoutStream(), level: "trace" },
      { stream: datadogLocalTransport, level: "trace" },
    ]),
  );
} else {
  // @ts-ignore
  logger = pino(loggerConfig);
}

if (isLocalScript) {
  try {
    const _req =
      typeof __webpack_require__ === "function"
        ? __non_webpack_require__
        : require;
    const chalk = _req("chalk");
    const { DateTime } = _req("luxon");

    const prettyStream = {
      write(message: string) {
        /* eslint-disable no-console */
        const tabString = (outString: string): string => {
          return outString.replace(/^/gm, "    ");
        };

        const obj = JSON.parse(message);
        const { name, msg, level, time, pid, hostname, err, ...rest } = obj;
        const levelMap: Record<number, string> = {
          10: chalk.gray("TRACE"),
          20: chalk.blue("DEBUG"),
          30: chalk.green("INFO"),
          40: chalk.yellow("WARN"),
          50: chalk.red("ERROR"),
          60: chalk.magenta("FATAL"),
        };
        const levelLog = levelMap[obj.level];
        const timeLog = DateTime.fromMillis(obj.time).toFormat("HH:mm:ss.SSS");
        const logInfo = chalk.gray(`[${timeLog}] (${name}):`);
        console.log(`${levelLog} ${logInfo} ${chalk.hex("#D9FFDF")(msg)}`);
        if (err) {
          const stack = err.stack ?? JSON.stringify(err);
          console.log(`${tabString(stack)}`);
        }
        for (const [key, value] of Object.entries(rest)) {
          let outString = `${key}: `;
          if (typeof value === "object") {
            outString += JSON.stringify(value, null, 2);
          } else {
            outString += value;
          }
          outString = tabString(outString);
          console.log(`${outString}`);
        }
        /* eslint-enable no-console */
      },
    } as DestinationStream;

    logger = pino({}, prettyStream);
  } catch {
    // chalk/luxon not available, keep default logger
  }
}

export const getLogger = (
  name: string,
  attributes: Record<string, any> = {},
): Logger => {
  return logger.child({ name, ...attributes });
};

export { logger as rootLogger };
